import {
	Box,
	Checkbox,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { DISABLED_CHANNELS, FORCED_CHANNELS } from "constants/communicationPreferences";
import React from "react";
import {
	Controller,
	FieldPath,
	useFormState,
	useWatch,
	type Control,
	type FieldValues,
	type UseFormSetValue,
	type UseFormTrigger
} from "react-hook-form";

const COMMUNICATION_COLUMNS = [
	{ label: "Email", field: "email" },
	{ label: "SMS", field: "sms" },
	{ label: "In-Portal", field: "portal" }
] as const;

type ChannelField = (typeof COMMUNICATION_COLUMNS)[number]["field"];

export interface CommunicationPreferencesFormValues extends FieldValues {
	communicationPreferences: Record<string, { portal: boolean; email: boolean; sms: boolean }>;
}

interface CommunicationPreferencesProps<T extends CommunicationPreferencesFormValues> {
	control: Control<T>;
	setValue: UseFormSetValue<T>;
	trigger: UseFormTrigger<T>;
	rows: readonly { systemName: string; name: string; description?: string | null }[];
	isLoading?: boolean;
	/** Hides the per-column "Select all"/"Unselect all" links. */
	hideSelectAll?: boolean;
	/** Pins the column headers while the rows scroll inside the nearest scrollable ancestor. */
	stickyHeader?: boolean;
	/** Shown instead of the table when there are no notification types to display. */
	emptyMessage?: string;
	/** How a row's description is surfaced — an info icon tooltip, or a line under the name. */
	descriptionVariant?: "tooltip" | "inline";
	/** When true, all forced-ON and disabled-channel restrictions are lifted (every checkbox is freely toggleable). */
	allowAllChannels?: boolean;
}

export default function CommunicationPreferences<T extends CommunicationPreferencesFormValues>({
	control,
	setValue,
	trigger,
	rows,
	isLoading = false,
	hideSelectAll = false,
	stickyHeader = false,
	descriptionVariant = "tooltip",
	emptyMessage = "Communication preferences are unavailable right now.",
	allowAllChannels = false
}: CommunicationPreferencesProps<T>) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const preferences = useWatch({ control, name: "communicationPreferences" as FieldPath<T> });
	const { errors } = useFormState({ control, name: "communicationPreferences" as FieldPath<T> });
	const commPrefsErrors = errors.communicationPreferences as
		Record<string, { message?: string }> | undefined;

	const isChannelDisabled = (systemName: string, field: string) =>
		!allowAllChannels && (DISABLED_CHANNELS[systemName]?.includes(field) ?? false);

	const isChannelForced = (systemName: string, field: string) =>
		!allowAllChannels && (FORCED_CHANNELS[systemName]?.includes(field) ?? false);

	const isChannelLocked = (systemName: string, field: string) =>
		isChannelDisabled(systemName, field) || isChannelForced(systemName, field);

	const hasAnySelected = (field: ChannelField) =>
		rows.some(
			(row) =>
				!isChannelLocked(row.systemName, field) &&
				(preferences as Record<string, Record<string, boolean>> | undefined)?.[row.systemName]?.[
					field
				]
		);

	const renderRowName = (row: (typeof rows)[number]) => {
		const description = row.description?.trim();

		if (!description) {
			return <Typography variant="text14Bold">{row.name}</Typography>;
		}

		if (descriptionVariant === "inline") {
			return (
				<>
					<Typography variant="text14Bold">{row.name}</Typography>
					<Typography variant="text12" color="secondary.light50" sx={{ mt: 0.5 }}>
						{description}
					</Typography>
				</>
			);
		}

		return (
			<Tooltip title={description}>
				<Typography variant="text14Bold" sx={{ display: "inline-block", cursor: "help" }}>
					{row.name}
				</Typography>
			</Tooltip>
		);
	};

	const renderChannelControl = (systemName: string, field: ChannelField) => (
		<Controller
			name={`communicationPreferences.${systemName}.${field}` as FieldPath<T>}
			control={control}
			render={({ field: formField }) => (
				<Checkbox
					size="small"
					checked={
						isChannelDisabled(systemName, field)
							? false
							: isChannelForced(systemName, field)
								? true
								: !!formField.value
					}
					disabled={isChannelLocked(systemName, field)}
					onChange={(event) => {
						formField.onChange(event.target.checked);
						trigger("communicationPreferences" as FieldPath<T>);
					}}
				/>
			)}
		/>
	);

	const handleToggleAll = (field: ChannelField, value: boolean) => {
		rows.forEach((row) => {
			if (isChannelLocked(row.systemName, field)) {
				return;
			}
			setValue(
				`communicationPreferences.${row.systemName}.${field}` as FieldPath<T>,
				value as never,
				{ shouldDirty: true, shouldValidate: false }
			);
		});
		trigger("communicationPreferences" as FieldPath<T>);
	};

	// Nothing to render when the notification types could not be loaded — the list is never
	// substituted with a hardcoded fallback.
	if (!isLoading && !rows.length) {
		return (
			<Box sx={{ py: 4, textAlign: "center" }}>
				<Typography variant="text14" color="secondary.light50">
					{emptyMessage}
				</Typography>
			</Box>
		);
	}

	if (isMobile) {
		return (
			<Box>
				{!hideSelectAll && (
					<Box sx={{ borderBottom: 1, borderColor: "grey.200", pb: 2.5 }}>
						{COMMUNICATION_COLUMNS.map((column) => (
							<Stack
								key={column.field}
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ height: 40, pr: 2 }}
							>
								<Typography variant="text12Uppercase" color="secondary.light50">
									{column.label}
								</Typography>
								{isLoading ? (
									<Skeleton variant="text" width={80} height={24} />
								) : (
									<Typography
										variant="text12"
										color="primary"
										sx={{
											cursor: "pointer",
											textDecoration: "underline"
										}}
										onClick={() => handleToggleAll(column.field, !hasAnySelected(column.field))}
									>
										{hasAnySelected(column.field) ? "Unselect all" : "Select all"}
									</Typography>
								)}
							</Stack>
						))}
					</Box>
				)}

				{rows.map((row) => (
					<Box
						key={row.systemName}
						sx={{
							py: 1.5,
							borderBottom: 1,
							borderColor: "divider"
						}}
					>
						<Box sx={{ mb: 1.5 }}>{renderRowName(row)}</Box>
						{COMMUNICATION_COLUMNS.map((column) => (
							<Stack
								key={`${row.systemName}-${column.field}`}
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ height: 40 }}
							>
								<Typography variant="text12Uppercase" color="secondary.light50">
									{column.label}
								</Typography>
								{isLoading ? (
									<Skeleton variant="rounded" width={24} height={24} />
								) : (
									renderChannelControl(row.systemName, column.field)
								)}
							</Stack>
						))}
						{commPrefsErrors?.[row.systemName]?.message && (
							<Typography variant="text12" color="error" data-comm-pref-error sx={{ mt: 0.5 }}>
								{commPrefsErrors[row.systemName].message}
							</Typography>
						)}
					</Box>
				))}
			</Box>
		);
	}

	return (
		<Table
			size="small"
			stickyHeader={stickyHeader}
			sx={{
				"& .MuiTableCell-root": {
					borderColor: "divider",
					px: 1,
					py: 2
				},
				"& .MuiTableHead-root .MuiTableCell-root": {
					borderColor: "grey.200",
					// MUI's sticky header defaults to background.default (grey) — keep it on the card colour.
					backgroundColor: "background.paper"
				}
			}}
		>
			<TableHead>
				<TableRow>
					<TableCell sx={{ verticalAlign: hideSelectAll ? "bottom" : "top" }}>
						<Typography variant="text12Uppercase" color="secondary.light50">
							Communication Type
						</Typography>
					</TableCell>
					{COMMUNICATION_COLUMNS.map((column) => (
						<TableCell
							key={column.field}
							sx={{
								width: 120,
								textAlign: "center",
								verticalAlign: "bottom"
							}}
						>
							<Stack alignItems="center" justifyContent="space-between" sx={{ height: "100%" }}>
								<Typography variant="text12Uppercase" color="secondary.light50">
									{column.label}
								</Typography>
								{!hideSelectAll &&
									(isLoading ? (
										<Skeleton variant="text" width={80} height={24} />
									) : (
										<Typography
											variant="text12"
											color="primary"
											sx={{ cursor: "pointer", textDecoration: "underline" }}
											onClick={() => handleToggleAll(column.field, !hasAnySelected(column.field))}
										>
											{hasAnySelected(column.field) ? "Unselect all" : "Select all"}
										</Typography>
									))}
							</Stack>
						</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map((row) => {
					const rowError = commPrefsErrors?.[row.systemName]?.message;
					// Drop the row's bottom padding when it owns an error so the message sits directly
					// under the name/description rather than a cell-padding gap away from it. The
					// "&&&" bumps specificity past the Table's own "& .MuiTableCell-root" padding rule.
					const erroredCellSx = rowError
						? { borderBottom: "none", "&&&": { paddingBottom: 0 } }
						: undefined;
					return (
						<React.Fragment key={row.systemName}>
							<TableRow>
								<TableCell sx={erroredCellSx}>{renderRowName(row)}</TableCell>
								{COMMUNICATION_COLUMNS.map((column) => (
									<TableCell
										key={`${row.systemName}-${column.field}`}
										align="center"
										sx={erroredCellSx}
									>
										{isLoading ? (
											<Skeleton
												variant="rounded"
												width={24}
												height={24}
												sx={{ mx: "auto", my: "6px" }}
											/>
										) : (
											renderChannelControl(row.systemName, column.field)
										)}
									</TableCell>
								))}
							</TableRow>
							{rowError && (
								<TableRow>
									<TableCell
										colSpan={COMMUNICATION_COLUMNS.length + 1}
										sx={{ "&&&": { paddingTop: "4px" } }}
									>
										<Typography variant="text12" color="error" data-comm-pref-error>
											{rowError}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					);
				})}
			</TableBody>
		</Table>
	);
}
